import { Responsible } from './types';

/**
 * La SRT admite un solo responsable por tipo profesional, y el tipo sale del
 * cargo (`idCargo`: R, H, M). Avisa sólo si el cargo elegido ya lo tiene otro
 * responsable del informe — antes avisaba al cargar cualquier segundo
 * responsable, aunque fuera de otro cargo (visto en preprod el 06/10/2026).
 */
export const debeAvisarResponsableDuplicado = (
    responsibles: Responsible[],
    cargo: number | string,
    idEditado?: number | string
): boolean =>
    !!cargo &&
    (responsibles ?? []).some(
        (r) => Number(r.idCargo) === Number(cargo) && String(r.id) !== String(idEditado ?? '')
    );

const tipoProfesionalCodeMap: Record<number, string> = {
    1: 'R',
    2: 'H',
    3: 'M',
    4: 'C'
};

const getCodeFromIdCargo = (responsible: Responsible): string | null => {
  return tipoProfesionalCodeMap[responsible.idCargo] || null;
};

// Check if at least one responsible of given code exists (based on idCargo)
export const hasResponsibleWithCode = (responsibles: Responsible[], code: string): boolean => {
  return responsibles.some(
    (responsible) =>
      getCodeFromIdCargo(responsible)?.toUpperCase() === code.toUpperCase()
  );
};

// Check if there are duplicates of a given code (e.g., multiple R or H)
export const hasDuplicateResponsibleCode = (responsibles: Responsible[], code: string): boolean => {
  const count = responsibles.reduce((acc, responsible) => {
    const mappedCode = getCodeFromIdCargo(responsible);
    if (mappedCode?.toUpperCase() === code.toUpperCase()) {
      return acc + 1;
    }
    return acc;
  }, 0);
  return count > 1;
};

// Get all duplicated codes (based on idCargo mapping)
export const getAllDuplicateCodes = (responsibles: Responsible[]): string[] => {
  const codeMap: Record<string, number> = {};

  responsibles.forEach((responsible) => {
    const code = getCodeFromIdCargo(responsible);
    if (!code) return;
    const upperCode = code.toUpperCase();
    codeMap[upperCode] = (codeMap[upperCode] || 0) + 1;
  });

  return Object.keys(codeMap).filter((code) => codeMap[code] > 1);
};